// Servicio para el Reporte 360 v2 - Detergentes y Químicos.
// Contrasta el consumo REAL (inventoryregistries, tipoMovimiento: 'consumo') contra el
// consumo TEÓRICO (reception.productosUsados, calculado por las fórmulas de los programas
// de lavado), por producto y por programa, con costos.

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const handleResponse = async (response, errorMsg) => {
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`${errorMsg} (HTTP ${response.status}): ${text || response.statusText}`);
  }
  return response.json();
};

// === Teórico: kilos procesados + productos por programa + productos totales ===
// Mismo endpoint que ya usa "Lotes vs. Ciclos" (POST /reception/reports/totals).
export const fetchTeorico = async ({ idLavanderia, dateRange }) => {
  const query = { 'lavanderia.id': idLavanderia, createdAt: dateRange };
  const response = await fetch(`${API_URL}/reception/reports/totals`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query }),
  });
  const json = await handleResponse(response, 'Error al obtener el consumo teórico');
  const data = json?.data || {};
  return {
    kilosProcesados: Number(data.kilosProcesados || 0),
    productosPorPrograma: Array.isArray(data.productosPorPrograma) ? data.productosPorPrograma : [],
    productosTotales: Array.isArray(data.productosTotales) ? data.productosTotales : [],
  };
};

// === Real: consumo tomado del inventario físico (tipoMovimiento: 'consumo') ===
export const fetchConsumoReal = async ({ idLavanderia, dateRange }) => {
  const params = new URLSearchParams();
  if (idLavanderia) params.append('lavanderiaId', idLavanderia);
  if (dateRange?.$gte) params.append('from', dateRange.$gte);
  if (dateRange?.$lte) params.append('to', dateRange.$lte);
  const response = await fetch(`${API_URL}/inventory-registries/totals-used?${params.toString()}`);
  const json = await handleResponse(response, 'Error al obtener el consumo real');
  return Array.isArray(json?.data) ? json.data : [];
};

// === Costos teóricos: costo por litro de cada producto (para valorizar el teórico) ===
// Prioridad: costo específico de la lavandería (LaundryProduct) > costo global (Product).
export const fetchCostosTeoricos = async ({ idLavanderia }) => {
  const [laundryProductsResp, productsResp] = await Promise.all([
    fetch(`${API_URL}/laundry-products?lavanderiaId=${encodeURIComponent(idLavanderia)}`),
    fetch(`${API_URL}/products?limit=1000&offset=1&query=${encodeURIComponent('{}')}`),
  ]);
  const [laundryProductsJson, productsJson] = await Promise.all([
    handleResponse(laundryProductsResp, 'Error al obtener costos por lavandería'),
    handleResponse(productsResp, 'Error al obtener catálogo de productos'),
  ]);

  const costoPorLitro = new Map();

  // 1) Base: costo global del producto
  (Array.isArray(productsJson?.data) ? productsJson.data : []).forEach((p) => {
    const id = p?._id || p?.id;
    const litros = Number(p?.presentacionLitros || 0);
    const costo = Number(p?.costo || 0);
    if (id && litros > 0) costoPorLitro.set(id, costo / litros);
  });

  // 2) Override: costo específico de la lavandería, si existe
  (Array.isArray(laundryProductsJson?.data) ? laundryProductsJson.data : []).forEach((lp) => {
    const id = lp?.producto?.id;
    const litros = Number(lp?.presentacionLitros || 0);
    const costo = Number(lp?.costo || 0);
    if (id && litros > 0) costoPorLitro.set(id, costo / litros);
  });

  return costoPorLitro;
};
